import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import {
  RowSelectionState,
  DropdownItem,
  Multiselect,
  M,
  Button,
} from 'design-system-web';
import { bulkAssignTeamsMutation } from 'graphql-queries';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { Assignable } from 'domains-work-order-type-contracts';

/** @deprecated Use Assignable instead */
type AdminUser = Assignable;

type TeamsSubmittingAction = 'assign' | 'unassign';

const SERVICE_REQUEST_SELECTION_LIMIT = 100;

type Props = {
  rowSelection?: RowSelectionState;
  assignableTeams: AdminUser[];
  refetch?: () => void;
};

export function BulkTeamsAssignAction({
  rowSelection,
  assignableTeams,
  refetch,
}: Props) {
  const { t } = useTranslation();
  const [bulkAssignTeams] = useMutation(bulkAssignTeamsMutation);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTeams, setSelectedTeams] = useState<AdminUser[]>([]);
  const [submittingAction, setSubmittingAction] =
    useState<TeamsSubmittingAction | null>(null);
  const isSubmitting = submittingAction !== null;

  const selectedWorkOrderIds = useMemo(() => {
    if (!rowSelection) {
      return [];
    }

    return Object.entries(rowSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([workOrderId]) => workOrderId);
  }, [rowSelection]);

  const openModal = async () => {
    if (selectedWorkOrderIds.length === 0) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.noSelection')
      );

      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      return;
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedTeams([]);
  };

  const submitBulkTeams = async (assigneeGroups: string[]) => {
    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      closeModal();

      return;
    }

    setSubmittingAction(
      assigneeGroups.length === 0 ? 'unassign' : 'assign'
    );

    try {
      const result = await bulkAssignTeams({
        variables: {
          serviceRequestIds: selectedWorkOrderIds,
          assigneeGroups,
        },
      });

      const { successCount, failureCount } = result.data?.bulkAssignTeams || {
        successCount: 0,
        failureCount: selectedWorkOrderIds.length,
      };

      if (failureCount === 0) {
        if (assigneeGroups.length === 0) {
          await window.Toast.show(
            t(
              'web.admin.serviceRequest.bulkActions.assignTeams.unassignAll.success',
              {
                count: successCount,
              }
            )
          );
        } else {
          await window.Toast.show(
            t('web.admin.serviceRequest.bulkActions.assignTeams.success', {
              count: successCount,
            })
          );
        }
      } else if (successCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.error')
        );
      } else {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.assignTeams.partial', {
            successCount,
            failureCount,
          }),
          5000
        );
      }

      if (refetch) {
        refetch();
      }
    } catch {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.assignTeams.error')
      );
    }

    setSubmittingAction(null);
    closeModal();
  };

  const handleConfirm = async () => {
    if (selectedTeams.length === 0) {
      return;
    }

    await submitBulkTeams(selectedTeams.map(team => team.value));
  };

  const handleUnassignAll = async () => {
    await submitBulkTeams([]);
  };

  return (
    <>
      <Button
        variant="secondary"
        size="large"
        onClick={openModal}
        testId="bulk-sr-teams-assign-select"
        style={{ marginRight: '0.5rem' }}
      >
        {t('web.admin.serviceRequest.bulkActions.assignTeams.selectTeams')}
      </Button>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        handleConfirm={handleConfirm}
        title={t('web.admin.serviceRequest.bulkActions.assignTeams.confirm.title')}
        confirmText={t(
          'web.admin.serviceRequest.bulkActions.assignTeams.confirm.button'
        )}
        cancelText={t(
          'web.admin.serviceRequest.bulkActions.assignTeams.cancel.button'
        )}
        loading={isSubmitting}
        confirmLoading={submittingAction === 'assign'}
        confirmActionDisabled={selectedTeams.length === 0 || isSubmitting}
        cancelActionDisabled={isSubmitting}
        isFullWidth
        footerActionsAfterConfirm={
          <Button
            variant="secondary"
            size="large"
            onClick={handleUnassignAll}
            disabled={isSubmitting}
            loading={submittingAction === 'unassign'}
            testId="bulk-sr-teams-unassign-all"
          >
            {t(
              'web.admin.serviceRequest.bulkActions.assignTeams.unassignAll.button'
            )}
          </Button>
        }
      >
        <M>
          {t('web.admin.serviceRequest.bulkActions.assignTeams.confirm.message', {
            count: selectedWorkOrderIds.length,
          })}
        </M>
        <Multiselect
          items={assignableTeams ?? []}
          value={selectedTeams.map(team => ({ value: team.value }))}
          onChange={(items: DropdownItem<string>[]) => {
            const matchedTeams = items
              .map(item =>
                (assignableTeams ?? []).find(team => team.value === item.value)
              )
              .filter((team): team is AdminUser => Boolean(team));

            setSelectedTeams(matchedTeams);
          }}
          placeholder={t(
            'web.admin.serviceRequest.bulkActions.assignTeams.selectTeams'
          )}
          doTranslation={false}
          isFullWidth
        />
      </ConfirmationModal>
    </>
  );
}
